import axios from 'axios'
import Config from '@config'
// Import once here so dayjs UTC/timezone plugins are configured before any hook runs
import dayjsConfigured, { toUtcIso } from '../utils/dayjs'

// ISO datetime: YYYY-MM-DDTHH:mm:ss  (with or without ms / timezone suffix)
const ISO_DATETIME_RE = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}/

/**
 * Recursively walks plain-object/array data and converts every ISO datetime
 * string to a UTC ISO string before it hits the API.
 */
function convertDatesToUtc(data: unknown): unknown {
  if (typeof data === 'string' && ISO_DATETIME_RE.test(data)) {
    return toUtcIso(data)
  }
  if (Array.isArray(data)) {
    return data.map(convertDatesToUtc)
  }
  if (data !== null && typeof data === 'object') {
    return Object.fromEntries(
      Object.entries(data as Record<string, unknown>).map(([k, v]) => [k, convertDatesToUtc(v)])
    )
  }
  return data
}

const api = axios.create({
  baseURL: Config.API_URL,
})

// Outgoing: convert local ISO datetimes → UTC before sending
// (Only applies to JSON bodies; FormData is handled field-by-field in each mutation)
api.interceptors.request.use((config) => {
  if (
    config.data &&
    !(config.data instanceof FormData) &&
    typeof config.data === 'object'
  ) {
    // Pre-serialise to a JSON string and set the Content-Type explicitly. On
    // RN 0.79 / Hermes, letting axios auto-serialise the object body resulted in
    // the API (NestJS) receiving an unparsed/empty body → 400 "email must be an
    // email, password must be a string" on sign-in. Sending an already-stringified
    // body + explicit Content-Type is robust regardless of the underlying cause.
    // FormData requests keep their own multipart Content-Type (excluded above).
    config.data = JSON.stringify(convertDatesToUtc(config.data))
    // Use AxiosHeaders.set(): direct bracket assignment
    // (config.headers['Content-Type'] = …) does NOT reliably reach the dispatched
    // request on axios 1.x, so the API received the stringified body without a
    // JSON Content-Type → parsed as text → 400 "email must be an email, password
    // must be a string" on sign-in. .set() writes to the normalized header store
    // axios actually serialises.
    config.headers.set('Content-Type', 'application/json')
  }
  return config
})

// Incoming: no transformation needed — the API returns UTC ISO strings (Z-suffix)
// and JS / dayjs.utc().tz() in fromApiDate() converts them to local time for display.

export { api, dayjsConfigured as dayjs }
